import { useState, useEffect } from "react";
import "../../assets/css/Users/UsersDrawer.css";
import api from "../../api/axios";
import Swal from "sweetalert2";
import { MdClose } from "react-icons/md";
import { FaEdit, FaTrashAlt, FaUser, FaEnvelope, FaUserShield, FaUserFriends, FaCalendarAlt, FaUserTie } from "react-icons/fa";

export default function UsersDrawer({
  user,
  isOpen,
  onClose,
  refreshUsers,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get("/users");
        setUsers(res.data || []);
      } catch (err) {
        console.error("Failed to load users", err);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    setFormData({
      ...user,
      manager_id: user.manager_id || "",
    });
    setEditMode(false);
    setActiveTab("overview");
  }, [user]);

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    role: "",
    manager_id: "",
  });

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.role) {
      newErrors.role = "Role is required";
    }

    const isSameUser =
      formData.manager_id &&
      String(formData.manager_id) === String(user?.id);

    if (isSameUser) {
      newErrors.manager_id = "User cannot be their own manager";
    }

    if (
      formData.role === "Sales Executive" &&
      !formData.manager_id
    ) {
      newErrors.manager_id = "Sales Executive must have a manager";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const saveUser = async () => {
    if (!validateForm()) return;

    try {
      await api.put(`/users/${user.id}`, formData);

      await Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "User updated successfully",
        timer: 1500,
        showConfirmButton: false,
      });

      if (refreshUsers) await refreshUsers();

      setEditMode(false);
      onClose();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
      });
    }
  };

  const deleteUser = async () => {
    const result = await Swal.fire({
      title: "Delete User?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/users/${user.id}`);

      await Swal.fire({
        icon: "success",
        title: "Deleted!",
        timer: 1500,
        showConfirmButton: false,
      });

      if (refreshUsers) await refreshUsers();

      onClose();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
      });
    }
  };

  const currentUser = JSON.parse(
    sessionStorage.getItem("user") || "{}"
);
  const role = String(currentUser?.role || "")
  .trim()
  .toLowerCase()
  .replace(/_/g, " ")
  .replace(/\s+/g, " ");

  const isSuperAdmin = role === "super admin"

  const permissions = {
    "super admin": { edit: true, delete: true },
    "sales manager": { edit: true, delete: false },
    "sales executive": { edit: false, delete: false },
    "lead generator": { edit: false, delete: false },
  };

  const permission = permissions[role] || { 
    edit: false, 
    delete: false, 
  };

  const getRoleClass = (roleName) => {
    if (!roleName) return "sales-executive";
    const lower = roleName.toLowerCase();
    if (lower.includes("admin")) return "super-admin";
    if (lower.includes("manager")) return "sales-manager";
    return "sales-executive";
  };

  const managerName =
    users.find((u) => u.id === formData.manager_id)?.name ||
    user?.manager_name ||
    "No Manager";

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="users-drawer" onClick={(e) => e.stopPropagation()}>

        {/* HEADER */}
        <div className="drawer-header-u">
          <div className="user-header-info">
            <div className="user-avatar-lg">
              {user?.name
                ?.split(" ")
                .slice(0, 2)
                .map((word) => word[0])
                .join("")
                .toUpperCase() || "US"}
            </div>

            <div className="user-header-content">
              <h2>{user?.name || "User Profile"}</h2>
              <p className="user-email">{user?.email}</p>
              <div className="user-badges">
                <span className={`role-chip ${getRoleClass(user?.role)}`}>
                  {user?.role || "Team Member"}
                </span>
              </div>
            </div>
          </div>

          <div className="user-header-actions">
            {!editMode && (
              <div className="header-icon-actions">
                {permission.edit && (
                  <button
                    className="icon-btn edit-btn"
                    onClick={() => setEditMode(true)}
                    title="Edit User"
                  >
                    <FaEdit />
                  </button>
                )}

                {permission.delete && (
                  <button
                    className="icon-btn delete-btn"
                    onClick={deleteUser}
                    title="Delete User"
                  >
                    <FaTrashAlt />
                  </button>
                )}
              </div>
            )}

            <button
              className="icon-btn close-btn"
              onClick={onClose}
              title="Close"
            >
              <MdClose />
            </button>
          </div>
        </div>

        {/* HERO SUMMARY BANNER */}
        {!editMode && (
          <div className="user-hero-banner">
            <div className="user-stat-card">
              <span className="user-stat-lbl">Role</span>
              <span className="user-stat-val">{user?.role || "Staff"}</span>
            </div>
            <div className="user-stat-card">
              <span className="user-stat-lbl">Reports To</span>
              <span className="user-stat-val">{managerName}</span>
            </div>
            <div className="user-stat-card">
              <span className="user-stat-lbl">Subordinates</span>
              <span className="user-stat-val">{user?.subordinates?.length || 0} Direct</span>
            </div>
          </div>
        )}

        {/* CONTENT */}
        <div className="drawer-content">
          {editMode ? (
            <div className="user-edit-form">
              <h3 className="section-title">Edit User Profile</h3>

              <div className="form-group">
                <label>Full Name</label>
                <input
                  name="name"
                  value={formData.name || ""}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className={errors.name ? "error" : ""}
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  name="email"
                  type="email"
                  value={formData.email || ""}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className={errors.email ? "error" : ""}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label>User Role</label>
                <select
                  name="role"
                  value={formData.role || ""}
                  onChange={handleChange}
                  className={errors.role ? "error" : ""}
                >
                  <option value="" disabled>Select Role</option>
                  <option value="Super Admin">Super Admin</option>
                  <option value="Sales Manager">Sales Manager</option>
                  <option value="Sales Executive">Sales Executive</option>
                  <option value="Lead Generator">Lead Generator</option>
                </select>
                {errors.role && <span className="error-text">{errors.role}</span>}
              </div>

              <div className="form-group">
                <label>Assigned Manager</label>
                <select
                  name="manager_id"
                  value={formData.manager_id || ""}
                  onChange={handleChange}
                  className={errors.manager_id ? "error" : ""}
                >
                  <option value="">No Manager (Independent)</option>
                  {users
                    .filter((u) => String(u.id) !== String(user?.id))
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </option>
                    ))}
                </select>
                {errors.manager_id && <span className="error-text">{errors.manager_id}</span>}
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setEditMode(false)}>
                  Cancel
                </button>
                <button type="button" className="btn-save" onClick={saveUser}>
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* ACCOUNT DETAILS SECTION */}
              <div className="user-section">
                <h4 className="section-title">
                  <FaUserShield style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Account Information
                </h4>
                <div className="section-card">
                  <div className="info-grid">
                    <div className="info-item">
                      <label><FaUser style={{ marginRight: '4px' }} />Full Name</label>
                      <p>{formData.name || "-"}</p>
                    </div>
                    <div className="info-item">
                      <label><FaEnvelope style={{ marginRight: '4px' }} />Email Address</label>
                      <p>{formData.email || "-"}</p>
                    </div>
                    <div className="info-item">
                      <label>Role</label>
                      <p>
                        <span className={`role-chip ${getRoleClass(formData.role)}`}>
                          {formData.role || "-"}
                        </span>
                      </p>
                    </div>
                    <div className="info-item">
                      <label><FaUserTie style={{ marginRight: '4px' }} />Manager</label>
                      <p>{managerName}</p>
                    </div>
                    <div className="info-item full-width">
                      <label><FaCalendarAlt style={{ marginRight: '4px' }} />Member Since</label>
                      <p>
                        {formData.created_at
                          ? new Date(formData.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          : "Not Available"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* TEAM SUBORDINATES SECTION */}
              <div className="user-section">
                <h4 className="section-title">
                  <FaUserFriends style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Direct Reports ({user?.subordinates?.length || 0})
                </h4>
                {!user?.subordinates || user.subordinates.length === 0 ? (
                  <div className="no-subordinates">
                    No direct reports assigned to this user.
                  </div>
                ) : (
                  <div className="subordinates-list">
                    {user.subordinates.map((subordinate) => (
                      <div className="subordinate-card" key={subordinate.id}>
                        <div className="sub-avatar">
                          {subordinate.name
                            ?.split(" ")
                            .slice(0, 2)
                            .map((word) => word[0])
                            .join("")
                            .toUpperCase()}
                        </div>
                        <div className="sub-info">
                          <h5 className="sub-name">{subordinate.name}</h5>
                          <p className="sub-email">{subordinate.email}</p>
                        </div>
                        <span className={`role-chip ${getRoleClass(subordinate.role)}`}>
                          {subordinate.role}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}